import { isCarouselContent } from '@mondaygpt/constants'
import type { InstagramPostContent } from '../types'

/**
 * Computes a hash of the publishable fields of an Instagram post.
 * Must match the server-side `computeContentHash` in instagram-publish.service.ts.
 *
 * Server branches on shape: carousels hash {caption, hashtags, mediaItems};
 * single-image / video posts hash {caption, hashtags, imageUrl, imageAttachmentId,
 * videoAttachmentId}. Client must mirror that so the lastPublishedAt comparison
 * doesn't flicker "republish needed" every time the user opens a carousel.
 *
 * Uses SubtleCrypto (available in all modern browsers) to compute SHA-256,
 * then takes the first 16 hex characters.
 */
export async function computeInstagramContentHash(content: InstagramPostContent): Promise<string> {
  const hashInput = isCarouselContent(content)
    ? JSON.stringify({
        caption: content.caption || '',
        hashtags: content.hashtags || [],
        mediaItems: content.mediaItems ?? [],
      })
    : JSON.stringify({
        caption: content.caption || '',
        hashtags: content.hashtags || [],
        imageUrl: content.imageUrl || '',
        imageAttachmentId: content.imageAttachmentId || '',
        videoAttachmentId: content.videoAttachmentId || '',
      })

  const encoder = new TextEncoder()
  const data = encoder.encode(hashInput)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  return hashHex.slice(0, 16)
}
